// Block data model — mirrors the design-handoff prototype exactly.

export type BlockType =
  | "heading"
  | "columns"
  | "article"
  | "editorial"
  | "table"
  | "qa"
  | "cards"
  | "panel"
  | "tales"
  | "quote"
  | "image";

export interface Block {
  id: string;
  type: BlockType;
  data: any;
}

export interface Head {
  volume: string;
  dispatch: string;
  date: string;
  title: string;
  tagline: string;
  strip: string;
}

export interface Doc {
  head: Head;
  blocks: Block[];
}

export function uid(): string {
  return "b" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

export function defaultHead(): Head {
  return {
    volume: "Vol. IV · No. 2",
    dispatch: "Official Dispatch of the Imperium",
    date: "YC 127 · Winter",
    title: "VOX IMPERIALIS",
    tagline: "— News from the Dronelands and beyond, twice per year —",
    strip: "Six-Month Review · 18 Pages · Distribution: Coalition Only · Compiled by CD",
  };
}

const DEFAULTS: Record<BlockType, any> = {
  heading: { kicker: "00 — Section", title: "Section Title", sub: "A short standfirst goes here.", serif: false },
  columns: {
    cols: 3,
    items: [
      { kicker: "Topic", head: "Headline one", body: "Body copy for the first column goes here." },
      { kicker: "Topic", head: "Headline two", body: "Body copy for the second column goes here." },
      { kicker: "Topic", head: "Headline three", body: "Body copy for the third column goes here." },
    ],
  },
  article: {
    cols: 2,
    imageLabel: "[ map / screenshot ]",
    paras: [
      "Open your article here. This flows across two columns.",
      "A second paragraph continues the piece.",
      "Close with the point you actually wanted to make.",
    ],
  },
  editorial: {
    author: "By Asher Elias, Executor",
    quote: '"A pull quote lifts the key line."',
    imageLabel: "[ portrait / plate ]",
    paras: [
      "<span style=\"float:left;font-family:'Cinzel',serif;font-weight:900;font-size:60px;line-height:.72;padding:6px 10px 0 0;color:var(--accent)\">T</span>he opening of the editorial, with a drop cap already set.",
      "A second paragraph of measured, dry commentary.",
    ],
  },
  table: {
    cols: ["System / Region", "Date", "ISK Lost", "Pilots", "Outcome"],
    rows: [
      ["M-OEE8, Immensea", "YC127.041", "2.1T", "4,180", "Field held"],
      ["FAT-6P, Detorid", "YC127.077", "870B", "2,050", "Objective met"],
    ],
  },
  qa: {
    rows: [
      { who: "VOX", a: false, text: "The question, put to the person in charge." },
      { who: "FC", a: true, text: "The answer, now that it is safe to give it." },
    ],
  },
  cards: {
    cols: 2,
    items: [
      { kicker: "Category", head: "Card title", body: "Short card body copy.", tag: "" },
      { kicker: "Category", head: "Card title", body: "Short card body copy.", tag: "" },
    ],
  },
  panel: {
    title: "Highlight Panel",
    sub: "A witty standfirst for the silly stuff.",
    callout: '"No."',
    bee: true,
    paras: ["The accent panel copy goes here, mascot alongside."],
  },
  tales: {
    title: "Nothing's As It Ceems",
    sub: "A dispatch from somewhere, possibly",
    footer: "— filed without further comment.",
    paras: ["The peculiar, deranged tale begins here.", "And meanders, as such tales do, to no particular end."],
  },
  quote: { text: '"A standalone pull quote for maximum drama."', cite: "— attribution" },
  image: { label: "[ full-width image / infographic ]", tall: false, caption: "" },
};

export function makeBlock(type: BlockType, override?: any): Block {
  const data = JSON.parse(JSON.stringify(DEFAULTS[type]));
  if (override) Object.assign(data, override);
  return { id: uid(), type, data };
}

export interface PaletteEntry {
  type: BlockType;
  label: string;
  data?: any; // default-data overrides applied on top of DEFAULTS[type]
}

export const PALETTE: PaletteEntry[] = [
  { type: "heading", label: "Section heading" },
  { type: "columns", label: "Column roundup" },
  { type: "article", label: "Article (single column)", data: { cols: 1 } },
  { type: "article", label: "Article (multi-column)", data: { cols: 2 } },
  { type: "editorial", label: "Editorial + quote" },
  { type: "table", label: "Data table" },
  { type: "qa", label: "Q&A interview" },
  { type: "cards", label: "Card grid" },
  { type: "panel", label: "Highlight panel (bee)" },
  { type: "tales", label: "Tales column" },
  { type: "quote", label: "Pull quote" },
  { type: "image", label: "Image slot" },
];
